import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import { useLatest } from 'ahooks';
import {
    BLOCK_USER_API_ROUTE,
    type action,
} from '~/routes/remix-api.user.block';

/**
 * Hook to block or unblock users.
 */
export function useBlockUser({
    onBlockSuccess,
    onBlockError,
    onUnblockSuccess,
    onUnblockError,
}: {
    onBlockSuccess?: () => void;
    onBlockError?: (message: string) => void;
    onUnblockSuccess?: () => void;
    onUnblockError?: (message: string) => void;
} = {}) {
    const wasBusyBlockRef = useRef(false);
    const wasBusyUnblockRef = useRef(false);
    const blockFetcher = useFetcher<typeof action>();
    const unblockFetcher = useFetcher<typeof action>();
    const latestOnBlockSuccess = useLatest(onBlockSuccess);
    const latestOnBlockError = useLatest(onBlockError);
    const latestOnUnblockSuccess = useLatest(onUnblockSuccess);
    const latestOnUnblockError = useLatest(onUnblockError);

    const blockUser = useCallback(
        (memberID: number) => {
            blockFetcher.submit(
                { memberID },
                {
                    action: BLOCK_USER_API_ROUTE,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
        },
        [blockFetcher],
    );

    const unblockUser = useCallback(
        (memberID: number) => {
            unblockFetcher.submit(
                { memberID, unblock: true },
                {
                    action: BLOCK_USER_API_ROUTE,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
        },
        [unblockFetcher],
    );

    // Run callback when block user mutation finishes.
    useEffect(() => {
        const isBusy = blockFetcher.state !== 'idle';
        const justFinished = wasBusyBlockRef.current && !isBusy;
        wasBusyBlockRef.current = isBusy;

        if (!justFinished || blockFetcher.data === undefined) {
            return;
        }

        const response = blockFetcher.data?.response;
        if (response?.__typename === 'BlockUsersResult') {
            latestOnBlockSuccess.current?.();
        } else if (response) {
            latestOnBlockError.current?.(response.Message);
        } else {
            latestOnBlockError.current?.(
                'An error occurred, please try again.',
            );
        }
    }, [blockFetcher, latestOnBlockSuccess, latestOnBlockError]);

    // Run callback when unblock user mutation finishes.
    useEffect(() => {
        const isBusy = unblockFetcher.state !== 'idle';
        const justFinished = wasBusyUnblockRef.current && !isBusy;
        wasBusyUnblockRef.current = isBusy;

        if (!justFinished || unblockFetcher.data === undefined) {
            return;
        }

        const response = unblockFetcher.data?.response;
        if (response?.__typename === 'BlockUsersResult') {
            latestOnUnblockSuccess.current?.();
        } else if (response) {
            latestOnUnblockError.current?.(response.Message);
        } else {
            latestOnUnblockError.current?.(
                'An error occurred, please try again.',
            );
        }
    }, [unblockFetcher, latestOnUnblockSuccess, latestOnUnblockError]);

    return {
        /**
         * One of the submit fetchers are submitting.
         */
        isSubmitting:
            blockFetcher.state !== 'idle' || unblockFetcher.state !== 'idle',
        /**
         * Block a user.
         */
        blockUser,
        /**
         * Unblock a user.
         */
        unblockUser,
    };
}
